import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, House, MagnifyingGlass, MapPin, Wine, Globe } from '@phosphor-icons/react';

export const NotFoundPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Parse the URL to determine what type of content was not found
  const pathSegments = location.pathname.split('/').filter(Boolean);
  
  const getNotFoundType = () => {
    if (pathSegments.includes('countries') && pathSegments.includes('regions')) {
      return 'region';
    } else if (pathSegments.includes('countries') && pathSegments.includes('cepages')) {
      return 'grapeVariety';
    } else if (pathSegments.includes('countries') && pathSegments.length === 2) {
      return 'country';
    } else if (pathSegments.includes('regions') && pathSegments.includes('cepages')) {
      return 'grapeVariety';
    } else if (pathSegments.includes('regions')) {
      return 'region';
    } else if (pathSegments.includes('cepages')) {
      return 'grapeVariety';
    } else if (pathSegments.includes('info')) {
      return 'info';
    } else {
      return 'general';
    }
  };

  const notFoundType = getNotFoundType();
  
  const getNotFoundContent = () => {
    switch (notFoundType) {
      case 'country':
        return {
          title: "Pays non trouvé",
          description: "Le pays que vous recherchez n'existe pas dans notre base de données.",
          icon: <Globe size={64} color="#4B164C" />,
          suggestions: [
            { text: "Explorer les pays disponibles", action: () => navigate('/info/countries'), icon: <Globe size={20} /> },
            { text: "Rechercher des vins", action: () => navigate('/search'), icon: <MagnifyingGlass size={20} /> },
            { text: "Retour à l'accueil", action: () => navigate('/'), icon: <House size={20} /> }
          ]
        };
      case 'region':
        return {
          title: "Région non trouvée",
          description: "Cette région viticole n'existe pas ou n'est pas encore disponible dans notre guide.",
          icon: <MapPin size={64} color="#4B164C" />,
          suggestions: [
            { text: "Explorer les régions disponibles", action: () => navigate('/info/regions'), icon: <MapPin size={20} /> },
            { text: "Rechercher des vins par région", action: () => navigate('/search'), icon: <MagnifyingGlass size={20} /> },
            { text: "Retour à l'accueil", action: () => navigate('/'), icon: <House size={20} /> }
          ]
        };
      case 'grapeVariety':
        return {
          title: "Cépage non trouvé",
          description: "Ce cépage n'existe pas ou n'est pas encore documenté dans notre guide.",
          icon: <Wine size={64} color="#4B164C" />,
          suggestions: [
            { text: "Explorer les cépages disponibles", action: () => navigate('/info/cepages'), icon: <Wine size={20} /> },
            { text: "Rechercher des vins par cépage", action: () => navigate('/search'), icon: <MagnifyingGlass size={20} /> },
            { text: "Retour à l'accueil", action: () => navigate('/'), icon: <House size={20} /> }
          ]
        };
      case 'info':
        return {
          title: "Page non trouvée",
          description: "Cette page d'information n'existe pas dans notre guide du vin.",
          icon: <MagnifyingGlass size={64} color="#4B164C" />,
          suggestions: [
            { text: "Guide du vin", action: () => navigate('/info'), icon: <Globe size={20} /> },
            { text: "Rechercher des vins", action: () => navigate('/search'), icon: <MagnifyingGlass size={20} /> },
            { text: "Retour à l'accueil", action: () => navigate('/'), icon: <House size={20} /> }
          ]
        };
      default:
        return {
          title: "Page non trouvée",
          description: "La page que vous recherchez n'existe pas ou a été déplacée.",
          icon: <MagnifyingGlass size={64} color="#4B164C" />,
          suggestions: [
            { text: "Guide du vin", action: () => navigate('/info'), icon: <Globe size={20} /> },
            { text: "Rechercher des vins", action: () => navigate('/search'), icon: <MagnifyingGlass size={20} /> },
            { text: "Retour à l'accueil", action: () => navigate('/'), icon: <House size={20} /> }
          ]
        };
    }
  };

  const content = getNotFoundContent();

  return (
    <main style={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '2rem',
      background: 'linear-gradient(135deg, #f8f6fa 0%, #e8d5e8 100%)',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{
        maxWidth: '600px',
        textAlign: 'center',
        background: '#fff',
        borderRadius: '20px',
        padding: '3rem',
        boxShadow: '0 8px 32px rgba(75,22,76,0.1)',
        border: '1px solid rgba(75,22,76,0.1)'
      }}>
        {/* Icon */}
        <div style={{ marginBottom: '2rem' }}>
          {content.icon}
        </div>

        {/* Error Code */}
        <div style={{
          fontSize: '6rem',
          fontWeight: '700',
          color: '#4B164C',
          marginBottom: '1rem',
          lineHeight: '1'
        }}>
          404
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: '2rem',
          color: '#4B164C',
          margin: '0 0 1rem 0',
          fontWeight: '600'
        }}>
          {content.title}
        </h1>

        {/* Description */}
        <p style={{
          fontSize: '1.1rem',
          color: '#666',
          margin: '0 0 2rem 0',
          lineHeight: '1.6'
        }}>
          {content.description}
        </p>

        {/* URL Info */}
        <div style={{
          background: '#f0e6f0',
          padding: '1rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          fontSize: '0.9rem',
          color: '#666',
          wordBreak: 'break-all'
        }}>
          <strong>URL tentée:</strong> {location.pathname}
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'grid',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {content.suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={suggestion.action}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                padding: '1rem 2rem',
                background: index === 0 ? '#4B164C' : 'transparent',
                color: index === 0 ? '#fff' : '#4B164C',
                border: `2px solid #4B164C`,
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                width: '100%'
              }}
              onMouseOver={e => {
                if (index === 0) {
                  e.currentTarget.style.background = '#6d2c7a';
                } else {
                  e.currentTarget.style.background = '#f0e6f0';
                }
              }}
              onMouseOut={e => {
                if (index === 0) {
                  e.currentTarget.style.background = '#4B164C';
                } else {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {suggestion.icon}
              {suggestion.text}
            </button>
          ))}
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: 'transparent',
            color: '#666',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '0.9rem',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={e => {
            e.currentTarget.style.background = '#f0e6f0';
            e.currentTarget.style.borderColor = '#4B164C';
            e.currentTarget.style.color = '#4B164C';
          }}
          onMouseOut={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = '#ddd';
            e.currentTarget.style.color = '#666';
          }}
        >
          <ArrowLeft size={16} />
          Retour en arrière
        </button>
      </div>
    </main>
  );
}; 